import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  type CSSProperties,
  type ComponentPublicInstance,
} from 'vue';

export type PopoverPlacement = 'top' | 'right' | 'bottom' | 'left';

export interface UsePopoverOptions {
  placement?: PopoverPlacement;
  crossAxisAlignment?: 'start' | 'end';
  gap?: number;
  getGapElement?: (referenceElement: HTMLElement) => HTMLElement | null;
  viewportMargin?: number;
  initialIsOpen?: boolean;
}

interface PopoverPosition {
  left: number;
  top: number;
}

const oppositePlacements: Record<PopoverPlacement, PopoverPlacement> = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right',
};

function getPopoverPosition(
  placement: PopoverPlacement,
  referenceRect: DOMRect,
  popoverRect: DOMRect,
  gap: number,
  viewportMargin: number,
  gapElementRect: DOMRect | null,
  crossAxisAlignment: 'start' | 'end',
): PopoverPosition {
  const gaps: Record<PopoverPlacement, number> = {
    top: getDynamicGap('top', gap, referenceRect, gapElementRect),
    right: getDynamicGap('right', gap, referenceRect, gapElementRect),
    bottom: getDynamicGap('bottom', gap, referenceRect, gapElementRect),
    left: getDynamicGap('left', gap, referenceRect, gapElementRect),
  };
  const positions: Record<PopoverPlacement, PopoverPosition> = {
    top: {
      left: crossAxisAlignment === 'start' ? referenceRect.left : referenceRect.right - popoverRect.width,
      top: referenceRect.top - popoverRect.height - gaps.top,
    },
    right: {
      left: referenceRect.right + gaps.right,
      top: crossAxisAlignment === 'start' ? referenceRect.top : referenceRect.bottom - popoverRect.height,
    },
    bottom: {
      left: crossAxisAlignment === 'start' ? referenceRect.left : referenceRect.right - popoverRect.width,
      top: referenceRect.bottom + gaps.bottom,
    },
    left: {
      left: referenceRect.left - popoverRect.width - gaps.left,
      top: crossAxisAlignment === 'start' ? referenceRect.top : referenceRect.bottom - popoverRect.height,
    },
  };
  const canFit: Record<PopoverPlacement, boolean> = {
    top: positions.top.top >= viewportMargin,
    right: positions.right.left + popoverRect.width <= window.innerWidth - viewportMargin,
    bottom: positions.bottom.top + popoverRect.height <= window.innerHeight - viewportMargin,
    left: positions.left.left >= viewportMargin,
  };
  const position = positions[canFit[placement] ? placement : oppositePlacements[placement]];
  const maximumLeft = Math.max(viewportMargin, window.innerWidth - popoverRect.width - viewportMargin);
  const maximumTop = Math.max(viewportMargin, window.innerHeight - popoverRect.height - viewportMargin);

  return {
    left: Math.min(Math.max(position.left, viewportMargin), maximumLeft),
    top: Math.min(Math.max(position.top, viewportMargin), maximumTop),
  };
}

function getDynamicGap(
  placement: PopoverPlacement,
  gap: number,
  referenceRect: DOMRect,
  gapElementRect: DOMRect | null,
): number {
  if (!gapElementRect) {
    return gap;
  }

  const distanceToElementEdge: Record<PopoverPlacement, number> = {
    top: referenceRect.top - gapElementRect.top,
    right: gapElementRect.right - referenceRect.right,
    bottom: gapElementRect.bottom - referenceRect.bottom,
    left: referenceRect.left - gapElementRect.left,
  };

  return Math.max(distanceToElementEdge[placement], 0) + gap;
}

export function usePopover(options: UsePopoverOptions = {}) {
  const {
    placement = 'right',
    crossAxisAlignment = 'start',
    gap = 8,
    getGapElement,
    viewportMargin = 16,
    initialIsOpen = false,
  } = options;
  const referenceElement = ref<HTMLElement | null>(null);
  const popoverElement = ref<HTMLElement | null>(null);
  const isPopoverOpen = ref(initialIsOpen);
  const popoverPosition = ref<PopoverPosition>({ left: viewportMargin, top: viewportMargin });

  const popoverStyle = computed<CSSProperties>(() => ({
    left: `${popoverPosition.value.left}px`,
    top: `${popoverPosition.value.top}px`,
  }));

  onMounted(() => {
    document.addEventListener('click', closePopoverOnOutsideClick);
    document.addEventListener('keydown', closePopoverOnEscape);
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    if (isPopoverOpen.value) {
      void nextTick(updatePopoverPosition);
    }
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', closePopoverOnOutsideClick);
    document.removeEventListener('keydown', closePopoverOnEscape);
    window.removeEventListener('resize', updatePopoverPosition);
    window.removeEventListener('scroll', updatePopoverPosition, true);
  });

  function closePopover(): void {
    isPopoverOpen.value = false;
  }

  async function togglePopover(): Promise<void> {
    isPopoverOpen.value = !isPopoverOpen.value;

    if (isPopoverOpen.value) {
      await nextTick();
      updatePopoverPosition();
    }
  }

  function closePopoverOnOutsideClick(event: MouseEvent): void {
    if (!isPopoverOpen.value || !(event.target instanceof Node)) {
      return;
    }

    if (referenceElement.value?.contains(event.target) || popoverElement.value?.contains(event.target)) {
      return;
    }

    closePopover();
  }

  function closePopoverOnEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      closePopover();
    }
  }

  function setPopoverElement(element: Element | ComponentPublicInstance | null): void {
    popoverElement.value = element instanceof HTMLElement ? element : null;
  }

  function updatePopoverPosition(): void {
    if (!isPopoverOpen.value || !referenceElement.value || !popoverElement.value) {
      return;
    }

    const referenceRect = referenceElement.value.getBoundingClientRect();
    const popoverRect = popoverElement.value.getBoundingClientRect();
    const gapElementRect = getGapElement?.(referenceElement.value)?.getBoundingClientRect() ?? null;
    popoverPosition.value = getPopoverPosition(
      placement,
      referenceRect,
      popoverRect,
      gap,
      viewportMargin,
      gapElementRect,
      crossAxisAlignment,
    );
  }

  return {
    closePopover,
    isPopoverOpen,
    popoverStyle,
    referenceElement,
    setPopoverElement,
    togglePopover,
  };
}
